import React, { useEffect, useMemo, useRef } from "react";
import { StyleSheet, View } from "react-native";
import Animated, {
  cancelAnimation,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import { BlurView } from "expo-blur";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";

export type ToastProps = {
  id?: string; // filled by provider
  title: string;
  message?: string;
  duration?: number; // seconds
  toastType?: "info" | "success" | "warning" | "error" | "fatal";
  onRequestClose?: () => void; // provider removes from stack
};

export function Toast({ title, message, duration = 7, toastType = "info", onRequestClose }: ToastProps) {
  const { colors } = useTheme();

  // Separate lifecyle vs. progress so hover never kills opacity
  const life = useSharedValue(0); // 0->1 (in), 1->0 (out)
  const progress = useSharedValue(1); // 1->0 countdown

  const totalMsRef = useRef(Math.max(600, duration * 1000));
  const exitingRef = useRef(false);
  const isHovering = useRef(false);

  const palette = useMemo(() => {
    const map: any = {
      error: colors.red,
      warning: colors.amber,
      success: colors.emerald,
      info: colors.indigo,
      fatal: colors.red,
    };
    return map[toastType] ?? colors.indigo;
  }, [toastType, colors]);

  const iconName: Record<NonNullable<ToastProps["toastType"]>, keyof typeof MaterialCommunityIcons.glyphMap> = {
    info: "information-outline",
    success: "check-circle-outline",
    warning: "alert-circle-outline",
    error: "alert-octagon-outline",
    fatal: "close-circle-outline",
  };

  const beginExit = () => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    cancelAnimation(progress);
    life.value = withTiming(0, { duration: 180 }, () => {
      onRequestClose && runOnJS(onRequestClose)();
    });
  };

  const startCountdown = () => {
    cancelAnimation(progress);
    progress.value = 1; // reset bar
    progress.value = withTiming(0, { duration: totalMsRef.current }, (finished) => {
      // Only exit if animation finished naturally AND user is not hovering
      if (finished && !isHovering.current) {
        runOnJS(beginExit)();
      }
    });
  };

  // mount
  useEffect(() => {
    life.value = withSpring(1, { damping: 16, stiffness: 240 });
    startCountdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // hover (web): pause; on leave, restart full duration
  const onHoverIn = () => {
    isHovering.current = true;
    cancelAnimation(progress);
    progress.value = withTiming(1, { duration: 200 }); // animate to full
  };
  const onHoverOut = () => {
    isHovering.current = false;
    startCountdown(); // restart from full duration
  };

  const wrapStyle = useAnimatedStyle(() => ({
    opacity: life.value,
    transform: [{ translateY: (1 - life.value) * 12 }, { scale: 0.98 + life.value * 0.02 }],
  }));

  const barStyle = useAnimatedStyle(() => ({
    transform: [{ scaleX: Math.max(0.001, progress.value) }],
  }));

  return (
    <Animated.View
      onPointerEnter={onHoverIn}
      onPointerLeave={onHoverOut}
      accessibilityRole="alert"
      style={[styles.wrap, wrapStyle]}
    >
      {/* Accent rail */}
      {/* <LinearGradient */}
      {/*   colors={[palette.border, palette.text]} */}
      {/*   start={{ x: 0, y: 0 }} */}
      {/*   end={{ x: 0, y: 1 }} */}
      {/*   style={styles.rail} */}
      {/* /> */}

      <BlurView
        tint={colors.name === "dark" ? "dark" : "light"}
        intensity={colors.name === "dark" ? 90 : 70}
        style={[styles.blur, { borderColor: palette.border }]}
      >
        <View style={[styles.iconWrap, { backgroundColor: palette.border, borderColor: palette.text }]}>
          <MaterialCommunityIcons name={iconName[toastType]} size={18} color="#fff" />
        </View>

        <View style={styles.body}>
          <ThemedText style={styles.title}>{title}</ThemedText>
          {message ? (
            <ThemedText numberOfLines={3} style={styles.msg}>
              {message}
            </ThemedText>
          ) : null}
        </View>

        <Button style={styles.closeBtn} icon="close" onPress={beginExit} />
      </BlurView>

      <View style={[styles.track, { borderColor: palette.border }]}>
        <Animated.View style={[styles.fill, { backgroundColor: palette.text }, barStyle]} />
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
    elevation: 3,
  },
  rail: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    zIndex: 1,
  },
  blur: {
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    justifyContent: "center",
  },
  body: { flex: 1, gap: 2 },
  title: { fontWeight: "700" },
  msg: { opacity: 0.85, lineHeight: 18 },
  closeBtn: { backgroundColor: "transparent", borderWidth: 0, minHeight: 28, minWidth: 28 },
  track: { height: 2, borderTopWidth: StyleSheet.hairlineWidth, bottom: 4, marginHorizontal: 10 },
  fill: { height: 2, transformOrigin: "left" },
});
